"use client";

import SignInModel from "@/components/modal/SignInModal";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Title, Caption } from "@/components/ui/typography";
import { ShoppingBag, X } from "lucide-react";

import PopUpCartItem from "./PopUpCartItem";
import PopUpCartFooter from "./PopUpCartFooter";
import { Cart } from "@/lib/SystemIcons";

import EmptyCart from "./EmptyCart";

import useCart from "@/app/hooks/useCart";
import { useSelector, useDispatch } from "react-redux";
import { useRouter } from "next/navigation";
import { useState, useEffect, useCallback, useRef } from "react";
import ComingSoonModal from "@/components/modal/CommingSoonModal";

import { useFetchData } from "@/app/hooks/query";
import { useQueryClient } from "@tanstack/react-query";
import { normalizeProductData, getCookieValue } from "@/app/utils/utility";
import cartService from "@/app/redux/cart-management/cart-management-service";
import {
  setStoreCartItems,
  setCartSynced,
  setCartInputValue,
} from "@/app/redux/cart-management/cart-management-slice";
import { getAddressList } from "@/app/redux/shipping-address-management/shipping-address-service";
import { setSelectedProductId } from "@/app/redux/product-management/product-management-slice";

/**
 * @module Cart/_components/PopUpCart
 */

/**
 * PopUpCart component that displays the shopping cart in a popup.
 * It includes the cart items, subtotal, and checkout functionality.
 *
 * @function PopUpCart
 * @returns {JSX.Element} The rendered popup cart component
 *
 * @example
 * // Basic usage
 * <PopUpCart />
 */
export default function PopUpCart() {
  const [showPopupCart, setShowPopupCart] = useState(false);
  const [showSignInPopup, setShowSignInPopup] = useState(false);
  const [comingSoonPopupModal, setComingSoonPopupModal] = useState(false);
  const [addressListData, setAddressListData] = useState([]);

  const router = useRouter();
  const dispatch = useDispatch();
  const { updateCartItem } = useCart();
  const queryClient = useQueryClient();

  const localCartItems = useSelector(
    (state) => state?.cart?.cartDetails?.items
  );
  const userEmail = useSelector(
    (state) => state?.auth?.userLoginDetails?.email
  );
  const cartSynced = useSelector(
    (state) => state.cart?.cartDetails?.isCartSynced
  );
  const cartInputValue = useSelector((state) => state?.cart?.cartInputValue);

  const isUserLoggedIn = Boolean(userEmail);
  const products = localCartItems || [];
  const isCartEmpty = products.length === 0;
  const totalQuantity = products?.reduce(
    (total, product) => total + product.quantity,
    0
  );

  /**
   * Handles the checkout process by redirecting to the checkout page if the user is logged in.
   * If not logged in, it opens the sign in popup.
   */
  const handleCheckoutClick = () => {
    setShowPopupCart(false);
    if (!isUserLoggedIn) {
      setShowSignInPopup(true);
    } else {
      router.push("/checkout");
    }
  };

  /**
   * Fetches server cart items and normalizes the product data.
   * Utilizes the useFetchData hook to retrieve cart details.
   *
   * @returns {Array} Normalized server cart items or an empty array if no products exist.
   */
  const { data: serverCartItems, refetch: serverCartItemsRefetch } =
    useFetchData(["cartItems"], () => cartService.getCartDetails(), {
      enabled: Boolean(getCookieValue("accessToken")),
      select: (data) => {
        if (!data?.products?.length) return [];
        return normalizeProductData(data?.products);
      },
    });

  useEffect(() => {
    getAddressListFun();
  }, []);

  const getAddressListFun = async () => {
    let response = await getAddressList({ address_type: "shipping" });
    if (response?.status) {
      setAddressListData(response?.data?.rows || []);
    } else {
      setAddressListData([]);
    }
  };

  useEffect(() => {
    if (cartSynced)
      dispatch(setStoreCartItems({ items: serverCartItems || [] }));
  }, [cartSynced, serverCartItems]);

  useEffect(() => {
    if (cartSynced && !isCartItemsDifferent(serverCartItems, products)) return;

    const storeCartItemsPresent = Array.isArray(products);
    const serverCartItemsPresent = Array.isArray(serverCartItems);
    if (serverCartItemsPresent && storeCartItemsPresent)
      populateCartItems(serverCartItems, products);
  }, [isUserLoggedIn, serverCartItems, products]);

  const populateCartItems = useCallback(
    async (serverCartItems, localCartItems) => {
      if (!serverCartItems?.length && !localCartItems?.length) {
        dispatch(setCartSynced(true));
        return;
      }

      if (cartSynced) return;

      // case 1 if serverCartItems is empty and localCartItems is not empty
      if (!serverCartItems?.length && localCartItems?.length) {
        if (isUserLoggedIn && !serverCartItems?.length) {
          dispatch(setStoreCartItems({ items: [] }));
        } else {
          await syncCartItems(localCartItems);
        }
        return;
      }

      // case 2 if serverCartItems is not empty and localCartItems is not empty
      if (serverCartItems?.length && localCartItems?.length) {
        const mergedCartItems = mergeCartItems(serverCartItems, localCartItems);
        await syncCartItems(mergedCartItems);
        return;
      }
      if (serverCartItems?.length === 1 && !localCartItems?.length) {
        dispatch(setStoreCartItems({ items: [] }));
      }
      // case 3 if serverCartItems is not empty and localCartItems is empty
      if (serverCartItems?.length && !localCartItems?.length) {
        dispatch(setStoreCartItems({ items: serverCartItems }));
      }
    },
    [serverCartItems, products]
  );

  function mergeCartItems(serverCartItems = [], localCartItems = []) {
    if (!isCartItemsDifferent(serverCartItems, localCartItems)) return;
    const mergeCartItems = [];

    localCartItems.forEach((localCartItem) => {
      const isProductPresent = serverCartItems.find(
        (item) => item.id === localCartItem.id
      );
      if (!isProductPresent) mergeCartItems.push(localCartItem);
      // else {
      //   const updatedQuantity = Math.min(
      //     localCartItem.quantity,
      //     isProductPresent.stockLeft
      //   );

      //   isProductPresent.quantity = updatedQuantity;
      // }
    });

    return mergeCartItems;
  }

  async function syncCartItems(cartItems = []) {
    if (!cartItems?.length || !isUserLoggedIn) return;
    try {
      const items = cartItems.map((item) => ({
        product_id: item.id,
        quantity: item.quantity,
      }));

      await cartService.addCartDetails({
        multiple: true,
        cartItems: items,
      });

      dispatch(setCartSynced(true));
      dispatch(setStoreCartItems({ items: [] }));
    } catch (error) {
      console.error(error);
      console.error("Something went wrong in bulk cart item updated");
    } finally {
      queryClient.invalidateQueries(["cartItems"]);
      dispatch(setStoreCartItems({ items: serverCartItems }));
    }
  }

  /**
   * Compares two arrays of cart items and returns true if they are different
   * @param {Object[]} serverCartItems - The cart items from the server
   * @param {Object[]} localCartItems - The cart items from local storage
   * @returns {boolean} Whether the two arrays are different
   */
  function isCartItemsDifferent(serverCartItems = [], localCartItems = []) {
    if (serverCartItems.length !== localCartItems.length) return true;

    const serverCartItemMap = new Map(
      serverCartItems.map((item) => [item.id, item])
    );

    for (const localCartItem of localCartItems) {
      const isServerCartItemPresent = serverCartItemMap.get(localCartItem.id);
      if (!isServerCartItemPresent) return true;
      if (localCartItem.quantity != isServerCartItemPresent.quantity)
        return true;
    }

    return false;
  }

  const handleDeleteCart = async (productId) => {
    //  dispatch(setCartInputValue(1));
    let copyOfCartInput = [...cartInputValue];
    let deletedCartItemIndex = cartInputValue?.findIndex(
      (item) => item.id === productId
    );
    copyOfCartInput.splice(deletedCartItemIndex, 1);
    dispatch(setCartInputValue(copyOfCartInput));
    const response = await updateCartItem({ productId, quantity: 0 });
    if (response?.status) {
      serverCartItemsRefetch();
    }
  };
  return (
    <>
      <SignInModel
        isOpen={showSignInPopup}
        onOpenChange={(e) => setShowSignInPopup(e)}
      ></SignInModel>
      <ComingSoonModal
        isOpen={comingSoonPopupModal}
        onOpenChange={(e) => setComingSoonPopupModal(e)}
      ></ComingSoonModal>

      <Sheet open={showPopupCart} onOpenChange={setShowPopupCart}>
        <SheetTrigger asChild>
          <div className="relative">
            <Cart className="h-5 w-5" />
            {totalQuantity > 0 && (
              <span className="absolute top-[-3px] right-[-15px] bg-system-primary text-white rounded-full w-[22px] h-[13px] text-[10px] pl-[10px] pr-[10px] flex items-center justify-center">
                {totalQuantity}
              </span>
            )}
          </div>
        </SheetTrigger>
        <SheetContent
          hideClose
          className="z-[999] w-full md:w-[766px] lg:w-[574px] sm:!max-w-[1000px] mx-auto p-0 border-l-0"
        >
          <header className="flex justify-between items-center p-4 h-[62px] md:h-[80px] border-b border-midnightIndigo-200">
            <div className="flex items-center">
              <Title size="lg" className="tracking-[-0.32px]">
                Your Cart
              </Title>
              <Caption className="bg-midnightIndigo-100 text-midnightIndigo-400 text-center ml-[10px] py-[5px] px-[8px] w-[42px] h-[31px] rounded">
                {totalQuantity < 99 ? totalQuantity : "99+"}
              </Caption>
            </div>
            <SheetClose>
              <X
                role="button"
                onClick={() => setShowPopupCart(false)}
                className="text-midnightIndigo-300 h-[24px] w-[24px]"
              />
            </SheetClose>
          </header>

          {isCartEmpty ? (
            <div onClick={() => setShowPopupCart(false)}>
              <EmptyCart />
            </div>
          ) : (
            <>
              <section
                data-lenis-prevent
                className="max-h-[90vh] overflow-y-scroll"
              >
                <ul className="mb-[270px] md:mb-[180px] lg:mb-[244px] ml-4 mr-5 lg:ml-0 lg:mr-0">
                  {products.map((product) => (
                    <li key={product.id}>
                      <PopUpCartItem
                        showSeparator={
                          products.length !== products.indexOf(product) + 1
                        }
                        onProductLabelClick={(id) => {
                          setShowPopupCart(false);
                          dispatch(setSelectedProductId(id));
                        }}
                        productDetails={product}
                        onChange={updateCartItem}
                        onDeleteConfirm={(productId) =>
                          handleDeleteCart(productId)
                        }
                      />
                    </li>
                  ))}
                </ul>
              </section>
              <PopUpCartFooter
                handleToCartClick={() => {
                  if (!isUserLoggedIn) {
                    setShowPopupCart(false);
                    setShowSignInPopup(true);
                  } else {
                    setShowPopupCart(false);
                  }
                }}
                addressListData={addressListData}
                handleCheckoutClick={() => {
                  if (!isUserLoggedIn) {
                    setShowPopupCart(false);
                    setShowSignInPopup(true);
                  } else {
                    handleCheckoutClick();
                  }
                }}
              />
            </>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
